import React = require("react");
import DatePicker from "react-datepicker";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {FormQuestion, FormQuestionAnswer, uuid, ValueType} from "../FeedbackFormModel";
import {Display} from "../../components/Display";

declare var AppTranslations: AppTranslations;

const NUMBER_UPPER_BOUND = 1000;

interface FormEntityUIProps {
    entity: FormQuestion;
    onChange?: (answer: FormQuestionAnswer) => void;
}

interface FormEntityUIState {
    selectValue: uuid;
    selectedValues: uuid[];
    typedText: string;
    selectedDate: Date;
}

export class FormEntityUI extends React.Component<FormEntityUIProps, FormEntityUIState> {

    constructor(props) {
        super(props);
        this.state = {
            selectValue: (((this.props.entity.availableResponseValues || [])
                .find((x) => x.defaultValue)) || {value: ""}).value,
            selectedValues: ((this.props.entity.availableResponseValues || [])
                .filter((x) => x.defaultValue))
                .map((x) => x.guid),
            typedText: "",
            selectedDate: new Date(),
        };
    }

    public render() {
        switch (this.props.entity.questionTypeGuid) {
            case ValueType.SELECT: {
                const hasDefaultOption = this.props.entity.availableResponseValues
                    .reduce((hasDefault, x) => x.defaultValue || hasDefault, false);

                const capitalize = (str: string) => {
                    return str ? str[0].toUpperCase() + str.slice(1) : "";
                };

                return (
                    <label className={"space-between"}>
                        <span className={"margin-right-10px font-size-1em"}>
                            {this.props.entity.text}
                            <Display isVisible={this.props.entity.required}>
                                <span className={"red"}>*</span>
                            </Display>
                        </span>
                        <select
                            className={"rounded-outlined"}
                            value={this.state.selectValue}
                            onChange={this.onSelectChange}
                        >
                            <Display isVisible={!hasDefaultOption}>
                                <option value={""} disabled={true}>
                                    {capitalize(AppTranslations.genericWords.select)}
                                </option>
                            </Display>
                            {this.props.entity.availableResponseValues.map((value) => (
                                <option key={value.guid} value={value.guid}>
                                    {capitalize(value.value)}
                                </option>
                            ))}
                        </select>
                    </label>
                );
            }
            case ValueType.CHECKBOX_GROUP: {
                return (
                    <label className={"space-between flex-wrap"}>
                        <span
                            className={"margin-right-10px font-size-1em inline-block"}
                            style={{
                                paddingBottom: this.props.entity.availableResponseValues.length > 3 ? 7 : undefined,
                            }}
                        >
                            {this.props.entity.text}
                            <Display isVisible={this.props.entity.required}>
                                <span className={"red"}>*</span>
                            </Display>
                        </span>
                        <div className={"checkbox-group"}>
                            {this.props.entity.availableResponseValues.map((value) => (
                                <label
                                    key={value.guid}
                                    className={"checkbox-group-element"}
                                >
                                    <input
                                        type={"checkbox"}
                                        checked={this.state.selectedValues.findIndex((x) => x === value.guid) > -1}
                                        onChange={this.onCheckboxGroupChange(value.guid)}
                                    />
                                    {value.value}
                                </label>
                            ))}
                        </div>
                    </label>
                );
            }
            case ValueType.NUMBER:
            case ValueType.TEXT: {
                return (
                    <label className={"space-between"}>
                        <span className={"margin-right-10px font-size-1em"}>
                            {this.props.entity.text}
                            <Display isVisible={this.props.entity.required}>
                                <span className={"red"}>*</span>
                            </Display>
                        </span>
                        <input
                            className={"rounded-outlined"}
                            value={this.state.typedText}
                            onChange={this.onEditTextChange}
                            type={
                                this.props.entity.questionTypeGuid === ValueType.TEXT
                                    ? "text"
                                    : "number"
                            }
                        />
                    </label>
                );
            }
            case ValueType.TEXTAREA: {
                return (
                    <label>
                        <span className={"margin-right-10px font-size-1em"}>
                            {this.props.entity.text}
                            <Display isVisible={this.props.entity.required}>
                                <span className={"red"}>*</span>
                            </Display>
                        </span>
                        <textarea
                            className={"rounded-outlined w-100 h-50px margin-top-5px no-resize"}
                            value={this.state.typedText}
                            onChange={this.onEditTextChange}
                        />
                    </label>
                );
            }
            case ValueType.DATE: {
                return (
                    <label className={"space-between"}>
                        <span className={"margin-right-10px font-size-1em"}>
                            {this.props.entity.text}
                            <Display isVisible={this.props.entity.required}>
                                <span className={"red"}>*</span>
                            </Display>
                        </span>
                        <DatePicker
                            className={"rounded-outlined"}
                            selected={this.state.selectedDate}
                            onChange={this.onDateChange}
                        />
                    </label>
                );
            }
            default: {
                return (
                    <label className={"space-between capitalize"}>
                        {AppTranslations.feedbackModal.notFound}
                    </label>
                );
            }
        }
    }

    private onSelectChange = (e) => {
        const value = e.target.value;
        this.setState({
            selectValue: value,
        }, () => {
            if (!this.props.onChange) {
                return;
            }

            this.props.onChange({
                questionGuid: this.props.entity.guid,
                value: [this.state.selectValue],
            });
        });
    }

    private onCheckboxGroupChange = (key) => {
        return (e) => {
            const checked = e.target.checked;
            this.setState({
                selectedValues: checked
                    ? [key, ...this.state.selectedValues]
                    : this.state.selectedValues.filter((x) => x !== key),
            }, () => {

                if (!this.props.onChange) {
                    return;
                }

                this.props.onChange({
                    questionGuid: this.props.entity.guid,
                    value: this.state.selectedValues.map<string>((value) => value),
                });
            });
        };
    }

    private onEditTextChange = (e) => {
        const value = e.target.value;
        if (this.props.entity.questionTypeGuid === ValueType.NUMBER && Number(value) >= NUMBER_UPPER_BOUND) {
            return;
        }

        this.setState({
            typedText: value,
        }, () => {

            if (!this.props.onChange) {
                return;
            }

            this.props.onChange({
                questionGuid: this.props.entity.guid,
                value: [
                    this.props.entity.questionTypeGuid === ValueType.NUMBER
                        ? Number(this.state.typedText)
                        : this.state.typedText,
                ],
            });
        });
    }

    private onDateChange = (selectedDate) => {
        this.setState({
            selectedDate,
        }, () => {
            if (!this.props.onChange) {
                return;
            }

            this.props.onChange({
                questionGuid: this.props.entity.guid,
                value: [this.state.selectedDate.toJSON()],
            });
        });
    }
}
